import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { getAuditLogsView } from '../../services/master.service';
import AuditLogChangeView from '../../atoms/AuditLogChangeView';
import * as CryptoJS from "crypto-js";
import { useLocation } from "react-router-dom";

function getFormatedDate(dateString) {
    if (dateString != null) {
        let dte = new Date(dateString);
        if (isNaN(dte.getTime())) return dateString;
        let year = dte.getFullYear();
        let month = String(dte.getMonth() + 1).padStart(2, '0');
        let day = String(dte.getDate()).padStart(2, '0');
        let hours = String(dte.getHours()).padStart(2, '0');
        let minutes = String(dte.getMinutes()).padStart(2, '0');
        let seconds = String(dte.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } else {
        return 'N/A';
    }
}

function AuditLogsView() {
    const [logId, setLogId] = useState('');
    const [userName, setUserName] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [decryptedTagName, setDecryptedTagName] = useState('');

    const handleConfirmOpen = (log_id, user_name) => {
        setLogId(log_id);
        setUserName(user_name);
        setIsConfirmOpen(true);
    };

    const handleConfirmClose = () => {
        setIsConfirmOpen(false);
    };

    const columns = [
        {
            field: 'serialNumber',
            headerName: 'Version',
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <span
                    style={{
                        background: '#e2e8f0',
                        color: '#334155',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: '12px',
                        display: 'inline-flex',
                        alignItems: 'center'
                    }}
                >
                    v{params.row.serialNumber}
                </span>
            )
        },
        {
            field: "tags",
            headerName: "Tag",
            width: 260,
            renderCell: (params) => (
                <span style={{ fontWeight: 600, color: '#1e293b', fontFamily: 'monospace' }}>
                    <i className="bi bi-tag-fill me-1 text-primary"></i>
                    {params.row.tags}
                </span>
            )
        },
        {
            field: "auditable_type",
            headerName: "Module",
            width: 170,
            renderCell: (params) => (
                <span
                    style={{
                        background: '#f8fafc',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500
                    }}
                >
                    {params.row.auditable_type || 'Asset'}
                </span>
            )
        },
        {
            field: "event",
            headerName: "Activity",
            width: 160,
            renderCell: (params) => {
                const ev = (params.row.event || '').toLowerCase();
                let bg = '#e0f2fe';
                let color = '#0369a1';
                let border = '#bae6fd';

                if (ev.includes('create') || ev.includes('add')) {
                    bg = '#dcfce7';
                    color = '#15803d';
                    border = '#bbf7d0';
                } else if (ev.includes('allocat')) {
                    bg = '#f3e8ff';
                    color = '#6b21a8';
                    border = '#e9d5ff';
                } else if (ev.includes('delet') || ev.includes('remove')) {
                    bg = '#fee2e2';
                    color = '#b91c1c';
                    border = '#fecaca';
                }

                return (
                    <span
                        style={{
                            background: bg,
                            color: color,
                            border: `1px solid ${border}`,
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'capitalize'
                        }}
                    >
                        {params.row.event}
                    </span>
                );
            }
        },
        {
            field: "user_name",
            headerName: "Username",
            width: 160,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#334155', fontWeight: 500 }}>
                    <i className="bi bi-person-circle text-secondary"></i>
                    <span>{params.row.user_name || 'System'}</span>
                </div>
            )
        },
        {
            field: "updated_at",
            headerName: "Date & Time",
            width: 160,
            renderCell: (params) => (
                <span style={{ color: '#475569', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <i className="bi bi-calendar3 text-muted"></i>
                    {getFormatedDate(params.row.updated_at)}
                </span>
            )
        },
        {
            field: "id",
            headerName: "Action",
            width: 160,
            sortable: false,
            renderCell: (params) => (
                <button
                    onClick={() => handleConfirmOpen(params.row.audit_id, params.row.serialNumber)}
                    style={{
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '6px 14px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(2, 132, 199, 0.25)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1.0'}
                >
                    <i className="bi bi-eye-fill"></i> View Change
                </button>
            )
        },
    ];

    const [items, setItems] = useState([]);
    const useQuery = () => new URLSearchParams(useLocation().search);
    const query = useQuery();
    const tag = query.get('tag');
    const method = query.get('method');
    const backUrl = query.get('back_url');
    const [back_url, setBackUrl] = useState(false);

    useEffect(() => {
        if (!tag) return;
        try {
            const decryptedBytes = CryptoJS.AES.decrypt(tag.replace(/ /g, '+'), process.env.REACT_APP_ENCRYPT_SECRET_KEY);
            const decrypt_data = JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8));
            const decryptModule = method ? method : decrypt_data.module;
            const decryptTag = decrypt_data.tag;
            setDecryptedTagName(decryptTag || '');
            setBackUrl(backUrl ? backUrl : '');

            let EncryptData = CryptoJS.AES.encrypt(JSON.stringify({
                'param_data': { 'tag': decryptTag, 'module': decryptModule },
                'authToken': sessionStorage.getItem('LoggedInToken'),
            }), process.env.REACT_APP_ENCRYPT_SECRET_KEY).toString();
            let oData = { 'token': EncryptData };

            getAuditLogsView(oData).then(res => {
                if (res?.data?.data?.length) setItems(res.data.data);
            });
        } catch (err) {
            console.error("Error decrypting audit log tag:", err);
        }
    }, [tag, method, backUrl]);

    const dataWithSerialNumbers = items.map((item, index) => ({
        ...item,
        serialNumber: index + 1,
    }));

    return (
        <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Container Card */}
            <div
                style={{
                    background: '#ffffff',
                    borderRadius: '16px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
                    border: '1px solid #e2e8f0',
                    overflow: 'hidden'
                }}
            >
                {/* Header Section */}
                <div
                    style={{
                        padding: '20px 28px',
                        borderBottom: '1px solid #e2e8f0',
                        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '12px'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                            style={{
                                width: '42px',
                                height: '42px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                fontSize: '20px',
                                boxShadow: '0 4px 6px -1px rgba(2, 132, 199, 0.3)'
                            }}
                        >
                            <i className="bi bi-clock-history"></i>
                        </div>
                        <div>
                            <h4 style={{ margin: 0, fontWeight: 700, color: '#0f172a', fontSize: '20px' }}>
                                Audit Log Trail
                            </h4>
                            <p style={{ margin: '2px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                                Track version changes and historical audit details for this asset tag
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {decryptedTagName && (
                            <span
                                style={{
                                    background: '#f1f5f9',
                                    border: '1px solid #cbd5e1',
                                    color: '#334155',
                                    padding: '6px 14px',
                                    borderRadius: '20px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <i className="bi bi-tag-fill text-primary"></i> TAG: {decryptedTagName}
                            </span>
                        )}

                        {(back_url !== '' && back_url !== undefined && back_url !== false) ? (
                            <a href={back_url + '?tag=' + tag} style={{ textDecoration: 'none' }}>
                                <button
                                    style={{
                                        background: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        color: '#475569',
                                        padding: '7px 16px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.15s ease-in-out'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                >
                                    <i className="bi bi-arrow-left"></i> Back
                                </button>
                            </a>
                        ) : (
                            <a href={'/audit-log-list'} style={{ textDecoration: 'none' }}>
                                <button
                                    style={{
                                        background: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        color: '#475569',
                                        padding: '7px 16px',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.15s ease-in-out'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#94a3b8'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
                                >
                                    <i className="bi bi-arrow-left"></i> Back
                                </button>
                            </a>
                        )}
                    </div>
                </div>

                {/* DataGrid Section */}
                <div style={{ padding: '16px 24px 24px 24px' }}>
                    <Box
                        sx={{
                            height: 520,
                            width: '100%',
                            '& .MuiDataGrid-root': {
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                fontFamily: 'inherit',
                                backgroundColor: '#ffffff'
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: '#f1f5f9',
                                color: '#1e293b',
                                fontWeight: 700,
                                fontSize: '13px',
                                borderBottom: '2px solid #cbd5e1'
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 700
                            },
                            '& .MuiDataGrid-row': {
                                borderBottom: '1px solid #f1f5f9',
                                '&:hover': {
                                    backgroundColor: '#f8fafc'
                                }
                            },
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                                borderRight: '1px solid #f1f5f9'
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc'
                            }
                        }}
                    >
                        <DataGrid
                            rows={dataWithSerialNumbers}
                            getRowId={(row) => row.audit_id}
                            columns={columns}
                            pageSize={50}
                            autoHeight
                            rowsPerPageOptions={[50]}
                        />
                    </Box>
                </div>
            </div>

            <AuditLogChangeView
                isLogId={logId}
                isVersion={userName}
                isOpen={isConfirmOpen}
                onClose={handleConfirmClose}
            />
        </div>
    );
}

export default AuditLogsView;
